<template>
  <section
    v-if="active"
    aria-labelledby="join-box-heading"
    class="flex flex-col gap-4 p-5 rounded-xl border border-gray-800 bg-white/5"
  >
    <h2 id="join-box-heading" class="text-3xl font-bold leading-tight">
      Choose your membership
    </h2>

    <form
      v-if="ready"
      method="post"
      :action="`${barUrl}/join/submit`"
      accept-charset="UTF-8"
      novalidate
      class="flex flex-col gap-4"
      @submit="onSubmit"
    >
      <input
        v-for="(value, name) in hiddenFields"
        :key="`hidden-${name}`"
        type="hidden"
        :name="name"
        :value="value"
      />

      <div v-if="groups.length > 1" class="flex flex-col gap-1">
        <div
          role="group"
          aria-label="Membership type"
          class="grid grid-cols-2 gap-1 p-1 rounded-lg bg-black/40"
        >
          <button
            v-for="group in groups"
            :key="`group-tab-${group.id}`"
            type="button"
            :aria-pressed="group.id === groupId"
            class="py-2 rounded-md text-base font-bold"
            :class="
              group.id === groupId
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:text-white'
            "
            @click="selectGroup(group)"
          >
            {{ group.title }}
          </button>
        </div>
        <p class="text-sm text-gray-400">{{ selectedGroup.note }}</p>
      </div>

      <fieldset class="grid gap-2 md:grid-cols-2 lg:grid-cols-1">
        <legend class="sr-only">Membership</legend>
        <label
          v-for="pricePoint in selectedGroup.pricePoints"
          :key="`price-point-${pricePoint.id}`"
          class="flex items-center gap-3 px-4 py-3 border rounded-lg cursor-pointer"
          :class="
            pricePointId === pricePoint.id
              ? 'border-green-500 bg-green-500/10'
              : 'border-gray-700 hover:border-gray-500'
          "
        >
          <input
            v-model="pricePointId"
            type="radio"
            name="price_point_id"
            :value="pricePoint.id"
            class="accent-green-500"
          />
          <span class="flex flex-col min-w-0">
            <span
              v-if="pricePoint.sale"
              class="text-sm font-bold text-green-500"
            >
              {{ pricePoint.sale }}
            </span>
            <span
              class="flex flex-wrap items-center gap-x-2 gap-y-1 text-lg font-bold leading-tight"
            >
              {{ pricePoint.title }}
              <span
                v-if="pricePoint.best"
                class="px-1.5 py-0.5 rounded bg-green-500 text-xs font-black uppercase tracking-wide text-black"
              >
                Best value
              </span>
            </span>
            <span v-if="pricePoint.charge" class="text-sm text-gray-400">
              Billed in one payment of {{ pricePoint.charge }}
            </span>
          </span>
          <span class="ml-auto pl-2 text-right whitespace-nowrap">
            <s
              v-if="pricePoint.wasPrice"
              class="mr-1 text-base text-gray-500"
              aria-label="Regular price"
            >
              {{ pricePoint.wasPrice }}
            </s>
            <span class="text-2xl font-bold">{{ pricePoint.price }}</span>
            <span class="text-sm text-gray-400">/{{ pricePoint.period }}</span>
          </span>
        </label>
      </fieldset>

      <div v-if="methods.length > 1" class="pt-4 border-t border-gray-800">
        <fieldset>
          <legend class="mb-1 text-base font-bold">Payment method</legend>
          <div
            class="grid gap-2"
            :style="{
              gridTemplateColumns: `repeat(${methods.length}, minmax(0, 1fr))`,
            }"
          >
            <label
              v-for="method in methods"
              :key="`method-${method.id}`"
              class="flex flex-col items-center justify-center gap-1.5 px-1 py-3 border rounded-lg cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-green-500"
              :class="
                cascade === method.id
                  ? 'border-green-500 bg-green-500/10 text-white'
                  : 'border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white'
              "
            >
              <input
                v-model="cascade"
                type="radio"
                name="cascade"
                :value="method.id"
                class="sr-only"
              />
              <span
                v-if="knownIcons(method).length"
                class="flex items-center h-7 -space-x-1.5"
              >
                <img
                  v-for="icon in knownIcons(method)"
                  :key="`icon-${method.id}-${icon}`"
                  :src="`/images/payment-logos/${icon}.svg`"
                  alt=""
                  class="h-full w-auto"
                  :class="{
                    'py-0.5': icon === 'visa' || icon === 'mastercard',
                  }"
                />
              </span>
              <span class="text-base font-bold whitespace-nowrap">
                {{ method.label }}
              </span>
            </label>
          </div>
        </fieldset>
      </div>
      <input v-else type="hidden" name="cascade" :value="cascade" />

      <template v-if="needsEmail">
        <input
          v-for="(value, name) in cardCredentials"
          :key="`credential-${name}`"
          type="hidden"
          :name="name"
          :value="value"
        />
        <div class="flex flex-col gap-1">
          <label for="join-box-email" class="text-base font-bold">Email</label>
          <input
            id="join-box-email"
            ref="emailEl"
            v-model.trim="email"
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            placeholder="you@example.com"
            required
            :aria-invalid="formError ? 'true' : undefined"
            aria-describedby="join-box-form-error"
            class="w-full px-3 py-2.5 rounded-lg bg-black/40 border text-lg text-white placeholder-gray-500"
            :class="formError ? 'border-red-500' : 'border-gray-700'"
          />
        </div>
      </template>
      <input v-else type="hidden" name="credentials" value="generated" />

      <p
        id="join-box-form-error"
        class="text-base text-red-500"
        :class="{ hidden: !formError }"
        role="alert"
      >
        {{ formError }}
      </p>

      <AppCtaButton
        type="submit"
        size="xl"
        class="w-full"
        :disabled="submitting"
      >
        Start membership
        <template v-if="selected?.charge">· {{ selected.charge }}</template>
      </AppCtaButton>

      <p
        class="-mt-2 flex items-center justify-center gap-2 text-sm text-gray-400"
      >
        <font-awesome-icon icon="fa-solid fa-lock" />
        You will be redirected to our secure payment page.
      </p>

      <p class="pt-3 border-t border-gray-800 text-sm text-gray-500">
        <template v-for="line in compliance" :key="line">{{ line }}.</template>
        Final price may differ due to currency exchange from USD.
        <a
          href="https://cvvhelp.net"
          target="_blank"
          rel="noopener"
          class="underline hover:text-gray-300"
        >
          Billing support
        </a>
      </p>
    </form>
    <slot v-else />
  </section>
  <slot v-else />
</template>

<script setup>
  import {
    browserLanguage,
    deviceType,
    rootDomain,
    tourLocation,
  } from "~/utils/creeper"
  import {
    asksForEmail,
    barSiteDomain,
    complianceLines,
    defaultPricePointId,
    isCompleteEmail,
    joinCredentials,
    membershipGroups,
    paymentMethods,
    readCookie,
    simulateSale,
  } from "~/utils/joinBox"

  defineOptions({ name: "TourClassicJoinBox" })

  // Shows the join form when active, and the default slot (today's join
  // button) until then, while bar's options load, and if they don't.
  const props = defineProps({
    active: { type: Boolean, default: false },
  })

  // The logos bar names that we have a file for.
  const ICONS = ["visa", "mastercard", "paypal_segpay", "check"]
  const knownIcons = (method) =>
    method.icons.filter((icon) => ICONS.includes(icon))

  const currentSite = inject("currentSite")
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const trackJoinClick = inject("trackJoinClick", () => {})
  const { barUrl, appEnv } = useRuntimeConfig().public
  const route = useRoute()

  // ?sale=1 / ?sale=0 previews the box with or without a sale, anywhere but
  // production (see simulateSale).
  const salePreview = computed(() =>
    appEnv !== "production" && ["0", "1"].includes(route.query.sale)
      ? route.query.sale
      : null,
  )

  const joinData = ref(null)
  const cascade = ref(null)
  const pricePointId = ref(null)
  const formError = ref("")
  const submitting = ref(false)
  const email = ref("")
  const emailEl = useTemplateRef("emailEl")
  // Only on sites listed in join_box_email_sites, so the email can be tried
  // on one site against the others.
  const siteAsksEmail = Boolean(currentSite.value?.joinBoxAsksEmail)
  const needsEmail = computed(
    () => siteAsksEmail && asksForEmail(cascade.value),
  )
  // Tells the two arms apart in bar's join_forms.
  const joinVariant = siteAsksEmail ? "box_email" : "box"

  const site = barSiteDomain(currentSite.value)
  const joinSource = tourLocation({
    ageRestricted: inAgeRestrictedLocation,
    variant: "box",
  })

  const pricePoints = computed(() =>
    simulateSale(
      joinData.value?.join_options?.price_points ?? [],
      salePreview.value,
    ),
  )
  const methods = computed(() =>
    paymentMethods(
      joinData.value?.join_options?.cascades,
      import.meta.client ? browserLanguage(navigator) : "en",
    ),
  )
  const groups = computed(() =>
    membershipGroups(pricePoints.value, {
      splitByAccess: site === "pornplus.com",
    }),
  )
  const groupId = ref(null)
  const selectedGroup = computed(
    () =>
      groups.value.find((group) => group.id === groupId.value) ??
      groups.value[0],
  )
  const selected = computed(() =>
    selectedGroup.value?.pricePoints.find(
      (pricePoint) => pricePoint.id === pricePointId.value,
    ),
  )
  // The terms of the membership being bought, next to the button.
  const compliance = computed(() =>
    complianceLines(
      pricePoints.value.filter(
        (pricePoint) => pricePoint.id === pricePointId.value,
      ),
    ),
  )

  // Switching tabs picks that tab's best offer, or its first membership.
  const selectGroup = (group) => {
    groupId.value = group.id
    pricePointId.value = (
      group.pricePoints.find((pricePoint) => pricePoint.best) ??
      group.pricePoints[0]
    )?.id
  }
  const ready = computed(
    () =>
      props.active && methods.value.length > 0 && pricePoints.value.length > 0,
  )

  // Set once loaded, in the browser: everything here is per visitor, and the
  // page around it is cached by CloudFront.
  const hiddenFields = ref({})
  const cardCredentials = ref({})

  const load = async () => {
    const stanSession = readCookie("stan_session")
    const query = new URLSearchParams(
      Object.entries({
        site,
        device_type: deviceType(navigator.userAgent),
        language: browserLanguage(navigator),
        stan: readCookie("stan"),
        session_id: stanSession,
        join_source: joinSource,
        join_variant: joinVariant,
      }).filter(([, value]) => value),
    )

    try {
      const response = await fetch(`${barUrl}/join/data?${query}`, {
        credentials: "include",
      })
      if (!response.ok) return

      const data = await response.json()
      const itsupClickId = readCookie("itsup_click_id")
      hiddenFields.value = {
        site,
        stan: readCookie("stan") ?? "",
        stan_session: data.session_id,
        stan_cid2: itsupClickId
          ? `itsup:${itsupClickId}`
          : (readCookie("stan_cid2") ?? ""),
        country: data.country,
        currency: data.currency,
        device_type: deviceType(navigator.userAgent),
        language: browserLanguage(navigator),
        join_source: joinSource,
        join_variant: joinVariant,
      }
      cardCredentials.value = joinCredentials(currentSite.value.slug)
      if (!stanSession) rememberStanSession(data.session_id)

      joinData.value = data
      cascade.value = methods.value[0]?.id ?? null
      pricePointId.value = defaultPricePointId(pricePoints.value)
      groupId.value = groups.value.find((group) =>
        group.pricePoints.some(
          (pricePoint) => pricePoint.id === pricePointId.value,
        ),
      )?.id
    } catch {
      // Leave today's join button in place.
    }
  }

  watch(
    () => props.active,
    (active) => {
      if (active && import.meta.client && !joinData.value) load()
    },
    { immediate: true },
  )

  const onSubmit = (event) => {
    if (submitting.value) {
      event.preventDefault()
      return
    }
    if (salePreview.value) {
      event.preventDefault()
      formError.value =
        "Sale preview: submitting is off. Remove ?sale= to join."
      return
    }
    // bar answers a bad submit with raw JSON, so check what it checks first.
    if (needsEmail.value && !isCompleteEmail(email.value)) {
      event.preventDefault()
      formError.value = "Enter your email address, like name@example.com."
      emailEl.value?.focus()
      return
    }
    if (!cascade.value || !pricePointId.value) {
      event.preventDefault()
      return
    }

    formError.value = ""
    submitting.value = true
    trackJoinClick()
  }

  // Coming back from the biller restores this page from the back/forward
  // cache with the button still disabled.
  const onPageShow = (event) => {
    if (event.persisted) submitting.value = false
  }
  onMounted(() => window.addEventListener("pageshow", onPageShow))
  onBeforeUnmount(() => window.removeEventListener("pageshow", onPageShow))

  // As the signup page does, for a visitor with no stan_session yet.
  const rememberStanSession = (id) => {
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    const domain = rootDomain(window.location.hostname)
    document.cookie = `stan_session=${id}; expires=${expires.toUTCString()}; domain=.${domain}; path=/`
  }
</script>
